import { formatCount } from '../../../../shared/sso-workspace/format'
// Shared wording for the Retention page and its override drawer.

export const fmtNumber = formatCount

export const months = n => `${fmtNumber(n)} ${Number(n) === 1 ? 'month' : 'months'}`
export const days = n => `${fmtNumber(n)} ${Number(n) === 1 ? 'day' : 'days'}`

// The periods an organization can override, in the order they show. unit
// formats a value; the platform default is read from the same key.
export const ORG_PERIODS = [
  { key: 'changesMonths', label: 'Change history (months)', header: 'Change history', unit: months },
  { key: 'requestDays', label: 'Request log (days)', header: 'Request log', unit: days },
  { key: 'viewsDays', label: 'Record views (days)', header: 'Record views', unit: days },
  { key: 'launchesDays', label: 'Course launches (days)', header: 'Course launches', unit: days }
]

// Every period's short name, for messages.
const NAMES = {
  changesMonths: 'Change history',
  triggerErrorsDays: 'Audit write errors',
  requestDays: 'Request log',
  viewsDays: 'Record views',
  launchesDays: 'Course launches',
  exportFilesDays: 'Export files',
  staffAlertDays: 'Staff alerts',
  notifyInboxDays: 'Notification inboxes',
  notifyDeliveryDays: 'Notification deliveries',
  notifyEventDays: 'Notification events'
}

// A legal hold keeps every per-organization row the retention run deletes
// (changes and named events, request log, record views, course launches);
// export files are platform-wide and still expire.
export const HOLD_COPY =
  'A legal hold keeps everything this organization has in the audit trail, including the request log, record views and course launches, until the hold is lifted. Files already exported still expire after the export-file period.'

export const NOT_AVAILABLE = "Retention settings aren't available on this server yet. They appear once its update has finished."

export const boundsLine = b => `Allowed: ${b.minMonths} to ${b.maxMonths} months. Days: 1 to ${fmtNumber(b.maxMonths * 31)}.`

// The allowed range for one period (content's env bounds: months
// minMonths..maxMonths, days 1..maxMonths x 31).
export const rangeOf = (key, b) => (key === 'changesMonths' ? { min: b.minMonths, max: b.maxMonths } : { min: 1, max: b.maxMonths * 31 })

// The inline message for a value outside the bounds, or null (blank, or no
// bounds known).
export const boundsProblem = (key, value, b) => {
  if (!b || value === '' || value === null || value === undefined) return null
  const n = Number(value)
  const isMonths = key === 'changesMonths'
  if (!Number.isInteger(n)) return `${NAMES[key]} must be a whole number of ${isMonths ? 'months' : 'days'}.`
  const { min, max } = rangeOf(key, b)
  if (n >= min && n <= max) return null
  return `${NAMES[key]} must be ${fmtNumber(min)} to ${fmtNumber(max)} ${isMonths ? 'months' : 'days'}.`
}

const RELOAD = 'Reload the page to see the current limits.'

// Plain copy for every code content's /audit/retention routes send (their
// own messages are written for developers). stale: the refusal means the
// page's view is out of date (a change made elsewhere), so reload it.
const PLAIN = {
  'retention.empty_override': 'Set at least one period or a legal hold, or remove the override.',
  'retention.hold_reason_required': 'Say why the hold is being lifted.',
  'retention.hold_lift_requires_reason': 'This organization is now on legal hold. Say why the hold is being lifted to remove it.',
  'retention.unknown_organization': 'This organization no longer exists.',
  'retention.override_not_found': 'This override was already removed.',
  'retention.not_while_impersonating': "You can't change retention settings while signed in as someone else. End the impersonation and try again.",
  'retention.retry': 'Someone else is changing retention settings right now. Try again in a moment.',
  'retention.not_available': NOT_AVAILABLE,
  'retention.forbidden': "You don't have permission to change retention settings.",
  'retention.signed_out': 'Your session has ended. Sign in again.',
  'retention.bad_request': "That change couldn't be saved. Please check the values and try again."
}
const STALE = ['retention.hold_lift_requires_reason', 'retention.unknown_organization', 'retention.override_not_found']

// retentionError(err, { bounds, sent, fallback }) -> { message, stale }.
// sent: the body that was refused, so an out-of-bounds refusal can name the
// field without reading the server's sentence.
export const retentionError = (err, { bounds, sent, fallback } = {}) => {
  const code = err && err.data && typeof err.data === 'object' ? err.data.code : undefined
  const stale = STALE.includes(code)
  if (code === 'retention.out_of_bounds') {
    const keys = Object.keys(sent || {}).filter(k => NAMES[k] && sent[k] !== null && sent[k] !== undefined)
    const problem = keys.map(k => boundsProblem(k, sent[k], bounds)).find(Boolean)
    if (problem) return { message: problem, stale }
    if (keys.length === 1) return { message: `${NAMES[keys[0]]} is outside what this server allows. ${RELOAD}`, stale }
    return { message: `A period is outside what this server allows. ${RELOAD}`, stale }
  }
  return { message: PLAIN[code] || fallback, stale }
}

// Organizations come back from content's list as an array or { data }.
export const listOf = v => (Array.isArray(v) ? v : Array.isArray(v && v.data) ? v.data : [])

export const orgName = (names, id) => names.get(Number(id)) || `Organization ${id}`
